import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useGql } from '@ah/design';
import { Header, PillTabs, PullToRefresh, ExplanationNote, type PillTab } from '@ah/design/mobile';
import {
  Mountain,
  BookOpen,
  Palette,
  Plane,
  Activity,
  Heart,
  Sparkles,
  type LucideIcon,
} from 'lucide-react';
import {
  MY_BUCKET_LIST_Q,
  SUGGESTED_BUCKET_LIST_ITEMS_Q,
  type BucketListItem,
  type BucketListCategory,
} from '../shared/index.ts';

const CATEGORY_ICON: Record<BucketListCategory, LucideIcon> = {
  adventure: Mountain,
  learning:  BookOpen,
  creative:  Palette,
  travel:    Plane,
  physical:  Activity,
  impact:    Heart,
};

const CATEGORY_LABEL: Record<BucketListCategory, string> = {
  adventure: 'Adventure',
  learning:  'Learning',
  creative:  'Creative',
  travel:    'Travel',
  physical:  'Physical',
  impact:    'Impact',
};

const TABS: PillTab[] = [
  { to: '/my-life/bucket-list', label: 'My List', end: true },
  { to: '/my-life/bucket-list/suggested', label: 'Suggested' },
];

function BucketListShell({
  children,
  onRefresh,
}: {
  children: React.ReactNode;
  onRefresh: () => Promise<unknown>;
}) {
  return (
    <div className="fixed inset-0 flex flex-col bg-background">
      <Header title="Bucket List" backTo="/" />
      <PillTabs tabs={TABS} />
      <PullToRefresh className="flex-1 px-3 py-3" onRefresh={onRefresh}>
        {children}
      </PullToRefresh>
    </div>
  );
}

export function MyBucketList() {
  const gql = useGql();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['bucketlist', 'mine'],
    queryFn: async () => {
      const res = await gql.request<{ myBucketList: BucketListItem[] }>(MY_BUCKET_LIST_Q);
      return res.myBucketList;
    },
  });

  return (
    <BucketListShell onRefresh={() => refetch()}>
      <ExplanationNote storageKey="bucketlist.my">
        Your bucket list is a private list of things you'd like to do, learn,
        or experience. I'll watch out for paths — events, people, books — that
        could help you get there.
      </ExplanationNote>
      {isLoading ? (
        <ListSkeleton />
      ) : !data || data.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="space-y-3">
          {data.map((item) => (
            <BucketListRow key={item.id} item={item} />
          ))}
        </div>
      )}
    </BucketListShell>
  );
}

export function SuggestedBucketList() {
  const gql = useGql();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['bucketlist', 'suggested'],
    queryFn: async () => {
      const res = await gql.request<{ suggestedBucketListItems: BucketListItem[] }>(
        SUGGESTED_BUCKET_LIST_ITEMS_Q,
        { limit: 36 },
      );
      return res.suggestedBucketListItems;
    },
  });

  return (
    <BucketListShell onRefresh={() => refetch()}>
      <ExplanationNote storageKey="bucketlist.suggested">
        Ideas worth considering. Tap one to see more — add it to keep it on
        your list, or skip and I'll bring up other ideas.
      </ExplanationNote>
      {isLoading ? (
        <ListSkeleton />
      ) : !data || data.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <Sparkles size={32} className="mx-auto mb-2 opacity-50" />
          <p className="text-sm">No fresh ideas right now. Check back later.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {data.map((item) => (
            <BucketListRow key={item.id} item={item} />
          ))}
        </div>
      )}
    </BucketListShell>
  );
}

function BucketListRow({ item }: { item: BucketListItem }) {
  const Icon = CATEGORY_ICON[item.category];
  const categoryLabel = CATEGORY_LABEL[item.category];

  return (
    <Link
      to={`/my-life/bucket-list/${item.id}`}
      className="flex gap-3 p-3 bg-card rounded-xl border border-border active:bg-accent"
    >
      {item.imageUrl ? (
        <img
          src={item.imageUrl}
          alt=""
          className="w-20 h-20 rounded-lg object-cover bg-muted shrink-0"
        />
      ) : (
        <div className="w-20 h-20 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
          <Icon size={28} className="text-primary" />
        </div>
      )}
      <div className="flex-1 min-w-0 flex flex-col gap-1">
        <span className="inline-block self-start bg-primary/10 text-primary rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold">
          {categoryLabel}
        </span>
        <h2 className="text-sm font-semibold leading-snug line-clamp-2">
          {item.label}
        </h2>
        <p className="text-xs text-muted-foreground line-clamp-2">
          {item.description}
        </p>
      </div>
    </Link>
  );
}

function EmptyState() {
  return (
    <div className="text-center py-16 text-muted-foreground">
      <Sparkles size={32} className="mx-auto mb-2 opacity-50" />
      <p className="text-sm">
        Your bucket list is empty.
      </p>
      <p className="text-xs mt-2">
        Discover ideas from the Suggested tab — when one resonates, tap{' '}
        <span className="font-semibold">Add to my list</span>.
      </p>
    </div>
  );
}

function ListSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <div
          key={i}
          className="flex gap-3 p-3 bg-card rounded-xl border border-border animate-pulse"
        >
          <div className="w-20 h-20 rounded-lg bg-muted shrink-0" />
          <div className="flex-1 min-w-0 flex flex-col gap-2">
            <div className="h-3 w-16 rounded bg-muted" />
            <div className="h-4 w-3/4 rounded bg-muted" />
            <div className="h-3 w-full rounded bg-muted" />
          </div>
        </div>
      ))}
    </div>
  );
}

